import React, { useEffect } from 'react'
import dayjs from 'dayjs'

import { isNull, isNullable } from '@app/utils'
import { DatePickerRenderer, RendererWrapper } from '@app/components/renderers'

import { ColumnRender } from '@app/types/table'

import { dateFormat } from '@app/constants'

import { getScheduleColumnChildren } from './utils'
import { columnKeyToApiMap, datePickerPropsConfig } from './configs'

import type { Column } from '@app/components/base/Table/types'
import type { Dayjs, FormatObject } from 'dayjs'
import type { CommonColumnsScheduleData, PatchCreatingTasksFn, UseScheduleColumnParams } from './types'

const scheduleColumnChildRenderFactory = <DataSourceType extends { key: number } & CommonColumnsScheduleData>(
  columnKey: keyof CommonColumnsScheduleData,
  patchCreatingTasks: PatchCreatingTasksFn
): ColumnRender<CommonColumnsScheduleData> => {
  const datePickerProps = datePickerPropsConfig[columnKey]
  const patchedApiKey = columnKeyToApiMap[columnKey]

  const handleChange = (value: Dayjs | null, _: unknown, record: DataSourceType): void => {
    if (isNull(value)) return
    // TODO: Вернуть таймзону, как будет готов бэк к обработке (убрать dateFormat.YYYY_MM_DDTHH_mm_ss из метода format())
    // Добавить формат с часовым поясом
    const formattedValue = dayjs(value).format(dateFormat.YYYY_MM_DDTHH_mm_ss)

    patchCreatingTasks({ runParametersIds: [record.key], [patchedApiKey]: formattedValue })
  }

  return function scheduleColumnChildRender (_, record) {
    // TODO: убрать лишнее создание переменных для startAt колонки
    const startAtDateString = record.startAt
    const startAtValue = startAtDateString
      ? dayjs(startAtDateString, datePickerPropsConfig.startAt.format as FormatObject)
      : undefined

    const isStartColumn = columnKey === 'startAt'

    const valueDateString = record[columnKey]
    const value = valueDateString ? dayjs(valueDateString, datePickerProps.format as FormatObject) : undefined

    const handleDisabledDate = (isStartColumn: boolean, startAtValue?: Dayjs) => {
      return (currentDate: Dayjs): boolean => {
        if (isStartColumn) return currentDate <= dayjs().endOf('day').subtract(1, 'day')

        if (isNullable(startAtValue)) return true

        return currentDate.isBefore(startAtValue)
      }
    }

    return (
      <RendererWrapper minWidth={ 200 }>
        <DatePickerRenderer<DataSourceType>
          { ...datePickerProps }
          disabled={ !isStartColumn && isNullable(startAtValue) }
          disabledDate={ handleDisabledDate(isStartColumn, startAtValue) }
          allowClear={ false }
          record={ record as DataSourceType }
          value={ value }
          onChange={ handleChange }
        />
      </RendererWrapper>
    )
  }
}

const modifyScheduleColumnChild = <DataSourceType extends { key: number } & CommonColumnsScheduleData>(
  scheduleColumnChild: Column<CommonColumnsScheduleData>,
  patchCreatingTasks: PatchCreatingTasksFn
): void => {
  const columnKey = scheduleColumnChild.key as keyof CommonColumnsScheduleData
  scheduleColumnChild.render = scheduleColumnChildRenderFactory<DataSourceType>(columnKey, patchCreatingTasks)
}

const modifyScheduleColumnChildren = <DataSourceType extends { key: number } & CommonColumnsScheduleData>(
  scheduleColumnChildren: Array<Column<CommonColumnsScheduleData>> | undefined,
  patchCreatingTasks: PatchCreatingTasksFn
): void => {
  if (scheduleColumnChildren) {
    scheduleColumnChildren.forEach(scheduleColumnChild =>
      modifyScheduleColumnChild<DataSourceType>(scheduleColumnChild, patchCreatingTasks)
    )
  }
}

export const useScheduleEditableColumns = <DataSourceType extends { key: number } & CommonColumnsScheduleData>({
  columns,
  patchCreatingTasks
}: UseScheduleColumnParams<DataSourceType>): Array<Column<DataSourceType>> => {
  useEffect(() => {
    const scheduleColumnChildren = getScheduleColumnChildren(columns)
    modifyScheduleColumnChildren<DataSourceType>(scheduleColumnChildren, patchCreatingTasks)
  }, [columns])

  return columns
}
